import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { redirect } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { PageReloadErrorMessage } from "@/components/common/PageReloadErrorMessage";
import { getVariantConfig } from "@/variants/index.server";

/**
 * `/` serves two audiences.
 *
 * Shopify always arrives with a `shop` (and usually `host`) query param — install, the admin
 * app frame, and every embedded deep link — so that traffic keeps redirecting to `/app` exactly
 * as before.
 *
 * Everything else used to follow the same redirect and land on `/app`, which throws 410 Gone
 * without a session. A 410 root tells crawlers the site is dead: Googlebot stops recrawling,
 * which in turn freezes the favicon Google has cached for the domain. So a plain visitor (or
 * crawler) now gets a real 200 page.
 *
 * All copy comes from the active variant's `publicSite` — AIOD and Free Gift are separately
 * listed apps on separate domains served from this one build, so nothing here may be hardcoded.
 */
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  if (url.searchParams.has("shop") || url.searchParams.has("host")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  const variant = getVariantConfig();

  return {
    appName: variant.appName,
    supportEmail: variant.branding.supportEmail,
    ...variant.publicSite,
  };
};

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: data?.appName ?? "App" },
  { name: "description", content: data?.tagline ?? "" },
];

export function ErrorBoundary() {
  return PageReloadErrorMessage();
}

export default function Index() {
  const { appName, tagline, supportEmail, listingUrl, icons } =
    useLoaderData<typeof loader>();

  return (
    <main
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        maxWidth: "34rem",
        margin: "0 auto",
        padding: "5rem 1.5rem",
        color: "#1a1a1a",
        lineHeight: 1.6,
      }}
    >
      {icons ? (
        <img
          src={icons.iconPng}
          alt=""
          width={72}
          height={72}
          style={{ borderRadius: "16px", display: "block" }}
        />
      ) : null}
      <h1 style={{ fontSize: "1.75rem", margin: "1.5rem 0 0.5rem" }}>
        {appName}
      </h1>
      <p style={{ margin: "0 0 1.5rem", color: "#4a4a4a" }}>{tagline}</p>
      {listingUrl ? (
        <p style={{ margin: 0 }}>
          <a href={listingUrl} style={{ color: "#2563eb" }}>
            View on the Shopify App Store
          </a>
        </p>
      ) : null}
      <p style={{ margin: "0.5rem 0 0" }}>
        <a href={`mailto:${supportEmail}`} style={{ color: "#2563eb" }}>
          {supportEmail}
        </a>
      </p>
      <p
        style={{ margin: "2.5rem 0 0", fontSize: "0.875rem", color: "#6a6a6a" }}
      >
        Already installed? Open the app from your Shopify admin.
      </p>
    </main>
  );
}
